import {
    Box,
    LinearProgress,
    LinearProgressProps,
    SxProps,
    TableCell,
    TableRow,
    Theme,
    Typography,
    useTheme
} from "@mui/material";
import { HoldingPeriodSchemeData } from "../../../redux-store/types/analytics";

interface TableBodyData {
    [key: string]: any
}

export const PortfolioHealthTableBody = (
    {
        tableBodyData
    }: any
): JSX.Element => {
    const theme = useTheme();

    const findBenchMarkStatus = tableBodyData.find((obj: { label: string, value: number | string }) => obj?.label?.toLowerCase() === 'ytd portfolio performance against benchmark')?.status;

    return (
        <>
            {tableBodyData?.map((obj: TableBodyData, i: number) => {
                return (
                    <TableRow
                        key={i}
                        sx={{
                            '& td': {
                                paddingTop: "20px",
                                paddingBottom: "15px",
                                borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                                ':first-of-type': {
                                    paddingLeft: "0px",
                                }
                            },
                        }}
                    >
                        <TableCell align="left">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    fontSize: '16px',
                                    color: theme.palette.text.primary,
                                    '&:hover': {
                                        color: theme.palette.primary[500]
                                    },
                                    // textTransform: 'capitalize'
                                })}
                            >
                                {obj?.label}
                            </Typography>
                        </TableCell>
                        <TableCell align="left">
                            {(i === tableBodyData?.length - 1 || i === tableBodyData?.length - 2) ?
                                obj?.status
                                :
                                <Box
                                    sx={(theme: Theme) => ({
                                        borderRadius: '50%',
                                        backgroundColor: obj?.status ? theme.palette.success[500] : theme.palette.error[500],
                                        width: '10px',
                                        height: '10px',
                                        ml: '15px'
                                    })}
                                >
                                </Box>
                            }
                        </TableCell>
                    </TableRow>
                )
            })}
        </>
    )
}

export const AssetAllocationGuidelinesBody = (
    {
        tableBodyData
    }: any
): JSX.Element => {
    const theme = useTheme();

    return (
        <>
            {tableBodyData?.map((obj: TableBodyData, i: number) => {

                const limits = `${obj?.lowerPer}-${obj?.upperPer}%`
                return (
                    <TableRow
                        key={i}
                        sx={{
                            '& td': {
                                paddingTop: "20px",
                                paddingBottom: "15px",
                                borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                                ':first-of-type': {
                                    paddingLeft: "0px",
                                }
                            },
                        }}
                    >
                        <TableCell align="left">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    fontSize: '16px',
                                    color: theme.palette.text.primary,
                                    '&:hover': {
                                        color: theme.palette.primary[500]
                                    },
                                    // textTransform: 'capitalize'
                                })}
                            >
                                {obj?.assetGroupName}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    color: theme.palette.text.primary,
                                    fontSize: '16px',
                                })}
                            >
                                {limits}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    // color: theme.palette.text.primary,
                                    fontSize: '16px',
                                    color: (obj?.actualPortfolio >= obj?.lowerPer && obj?.actualPortfolio <= obj?.upperPer) ? theme.palette.success[500] : theme.palette.error[500]
                                })}
                            >
                                {!Number.isNaN(Number(obj?.actualPortfolio ?? 0)) ?
                                    Math.round((obj?.actualPortfolio ?? 0) * 100) / 100 : '0'}%
                            </Typography>
                        </TableCell>
                    </TableRow>
                )
            })}
        </>
    )
}

const LinearProgressWithLabel = ({ value }: { value: number }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center'
            }}
        >
            <Box sx={{ width: "100%", marginRight: "12px" }}>
                <LinearProgress
                    variant="determinate"
                    value={value}
                    sx={(theme: Theme) => ({
                        '&.MuiLinearProgress-root': {
                            borderRadius: "15px",
                            backgroundColor: theme.palette.grey[200],
                            height: "6px"
                        }
                    })}
                />
            </Box>
            <Box>
                <Typography
                    variant="body_500"
                    sx={(theme: Theme) => ({
                        fontWeight: 700,
                        lineHeight: "20px",
                        letterSpacing: "0.02px",
                        color: theme.palette.primary[500]
                    })}
                >
                    {value}%
                </Typography>
            </Box>
        </Box>
    );
}

export const ContributionAnalysisTableBody = ({ data, type }: {
    data: {
        id: number;
        sector?: string;
        issuer?: string;
        portfolioWeight: number;
        portfolioReturns: number;
        cumulativeWeight: number;
    }[],
    type: string
}): JSX.Element => {
    return <>
        {data.map((obj: {
            id: number;
            sector?: string;
            issuer?: string;
            portfolioWeight: number;
            portfolioReturns: number;
            cumulativeWeight: number;
        }) => {
            return (
                <TableRow
                    key={obj.id}
                    sx={(theme: Theme) => ({
                        '& td': {
                            paddingTop: "30px",
                            paddingBottom: "30px",
                            borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                        },
                        '& td:first-of-type': {
                            paddingLeft: "0px"
                        },
                    })}
                >
                    <TableCell align="left">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                lineHeight: "20px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.primary,
                                '&:hover': {
                                    color: theme.palette.primary[500]
                                },
                                textTransform: 'capitalize'
                            })}
                        >
                            {type === "sector" ? (obj?.sector?.toLowerCase() ?? "-") : (obj?.issuer?.toLowerCase() ?? "-")}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                lineHeight: "20px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.primary
                            })}
                        >
                            {`${obj?.portfolioWeight}%` ?? "-"}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                lineHeight: "20px",
                                letterSpacing: "0.02px",
                                color: theme.palette.text.primary
                            })}
                        >
                            {`${obj?.portfolioReturns}%` ?? "-"}
                        </Typography>
                    </TableCell>
                    <TableCell align="center">
                        <LinearProgressWithLabel value={obj?.cumulativeWeight ?? 0} />
                    </TableCell>
                </TableRow>
            )
        })}
    </>
}

export const PrudentialNormsBody = (
    {
        tableBodyData
    }: any
): JSX.Element => {
    const theme = useTheme();

    return (
        <>
            {tableBodyData?.map((obj: TableBodyData, i: number) => {
                return (
                    <TableRow
                        key={i}
                        sx={{

                            '& td': {
                                paddingTop: "20px",
                                paddingBottom: "15px",
                                borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                                backgroundColor: (obj?.assetClass === 'Debt' || obj?.assetClass === 'Equity') ? theme.palette.primary[50] : '',
                                ':first-of-type': {
                                    paddingLeft: "10px",
                                }
                            },
                        }}
                    >
                        <TableCell align="left">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    fontSize: '16px',
                                    fontWeight: (obj?.assetClass === 'Debt' || obj?.assetClass === 'Equity') ? 700 : 500,
                                    color: theme.palette.text.primary,
                                    '&:hover': {
                                        color: (obj?.assetClass === 'Debt' || obj?.assetClass === 'Equity') ? '' : theme.palette.primary[500]
                                    },
                                    // textTransform: 'capitalize'
                                })}
                            >
                                {/* {obj?.assetClass?.toLowerCase()} */}
                                {obj?.assetClass ?? ""}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    color: theme.palette.text.primary,
                                    fontSize: '16px',
                                })}
                            >
                                {obj?.limits}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    // color: theme.palette.text.primary,
                                    fontSize: '16px',
                                    color: obj?.flag ? theme.palette.success[500] : theme.palette.error[500]
                                })}
                            >
                                {(obj?.actualPortfolio)?.toFixed(2)}{(obj?.assetClass === 'Debt' || obj?.assetClass === 'Equity') ? '' : '%'}
                            </Typography>
                        </TableCell>
                    </TableRow>
                )
            })}
        </>
    )
}

export const MaxAllocationMFBody = ({ tableBodyData }: {
    tableBodyData: {
        label: string;
        lowerPer: number;
        upperPer: number;
        status: boolean;
    }[]
}
): JSX.Element => {
    const theme = useTheme();

    return (
        <>
            {tableBodyData?.map((obj: TableBodyData, i: number) => {
                return (
                    <TableRow
                        key={i}
                        sx={{
                            '& td': {
                                paddingTop: "20px",
                                paddingBottom: "15px",
                                borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                                ':first-of-type': {
                                    paddingLeft: "0px",
                                }
                            },
                        }}
                    >
                        <TableCell align="left">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    fontSize: '16px',
                                    color: theme.palette.text.primary,
                                    '&:hover': {
                                        color: theme.palette.primary[500]
                                    },
                                    // textTransform: 'capitalize'
                                })}
                            >
                                {obj?.label ?? ""}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    color: theme.palette.text.primary,
                                    fontSize: '16px',
                                })}
                            >
                                {obj?.lowerPer ?? 0}-{obj?.upperPer ?? 0}%
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            {/* <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    color: theme.palette.text.primary,
                                    fontSize: '16px',
                                })}
                            >
                                {obj?.current}
                            </Typography> */}
                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                                <Box
                                    sx={(theme: Theme) => ({
                                        borderRadius: '50%',
                                        backgroundColor: obj?.status ? theme.palette.success[500] : theme.palette.error[500],
                                        width: '10px',
                                        height: '10px',
                                        // ml: '15px'
                                        // textAlign: 'center'
                                    })}
                                />
                            </Box>

                        </TableCell>
                    </TableRow>
                )
            })}
        </>
    )
}

export const HoldingPeriodSchemeBody = ({
    tableBodyData,
}: {
    tableBodyData: HoldingPeriodSchemeData[],
}): JSX.Element => {
    const theme = useTheme();

    return (
        <>
            {tableBodyData?.map((obj: TableBodyData, i: number) => {
                return (
                    <TableRow
                        key={i}
                        sx={{
                            '& td': {
                                paddingTop: "20px",
                                paddingBottom: "15px",
                                borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                                ':first-of-type': {
                                    paddingLeft: "0px",
                                }
                            },
                        }}
                    >
                        <TableCell align="left">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    fontSize: '16px',
                                    color: theme.palette.text.primary,
                                    '&:hover': {
                                        color: theme.palette.primary[500]
                                    },
                                    // textTransform: 'capitalize'
                                })}
                            >
                                {obj?.schemeName}
                            </Typography>
                        </TableCell>
                        <TableCell align="left">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    color: theme.palette.text.primary,
                                    fontSize: '16px',
                                })}
                            >
                                {obj?.saleDate}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography
                                variant="body_500"
                                sx={(theme: Theme) => ({
                                    color: theme.palette.text.primary,
                                    fontSize: '16px',
                                })}
                            >
                                {!Number.isNaN(Number(obj?.quantity ?? 0)) ?
                                    Math.round((obj?.quantity ?? 0) * 100) / 100 : '0'}
                            </Typography>
                        </TableCell>
                    </TableRow>
                )
            })}
        </>
    )
}